import type { TokenHealthState } from '@/api/adminClient';

const MINUTE = 60_000;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

function plural(count: number, unit: string): string {
  return `${count} ${unit}${count === 1 ? '' : 's'} ago`;
}

/** "5 minutes ago", "12 days ago". `Never` when there is no usable time. */
export function formatRelative(iso: string | undefined, now: number): string {
  const at = iso ? Date.parse(iso) : NaN;
  if (!Number.isFinite(at)) return 'Never';
  const age = Math.max(0, now - at);
  if (age < MINUTE) return 'just now';
  if (age < HOUR) return plural(Math.floor(age / MINUTE), 'minute');
  if (age < DAY) return plural(Math.floor(age / HOUR), 'hour');
  return plural(Math.floor(age / DAY), 'day');
}

/** The full time for a hover title, US format in the viewer's zone. */
export function formatAbsolute(iso: string | undefined): string | undefined {
  const at = iso ? new Date(iso) : undefined;
  if (!at || Number.isNaN(at.getTime())) return undefined;
  return new Intl.DateTimeFormat('en-US', { dateStyle: 'medium', timeStyle: 'short' }).format(at);
}

/** "3:42 PM" in the viewer's time zone (or `timeZone`, for tests). */
export function formatUpdatedAt(iso: string, timeZone?: string): string {
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return '';
  return new Intl.DateTimeFormat('en-US', { hour: 'numeric', minute: '2-digit', ...(timeZone ? { timeZone } : {}) }).format(at);
}

type Row = { counts: Record<TokenHealthState, number>; attention: unknown[] };

/** Anything failing or needing a reconnect, whether or not the list names it. */
export function needsAttention(row: Row): boolean {
  return row.attention.length > 0 || row.counts.failing > 0 || row.counts.needs_reconnect > 0;
}

/** Rows that need attention first; otherwise by title. */
export function sortByAttention<T extends Row & { title: string }>(rows: T[]): T[] {
  return [...rows].sort((a, b) => Number(needsAttention(b)) - Number(needsAttention(a)) || a.title.localeCompare(b.title));
}
